import { addDefaultProps, addDisplayName, addProps, flowMax } from 'ad-hok'
import cx from 'classnames'
import { FC } from 'react'
import { Trans } from 'react-i18next'
import { createUseStyles } from 'react-jss'
import { Link } from 'react-router-dom'
import { addNavigationState } from 'shared/src/utils/addNavigationState'
import { ReactComponent as RodeoLogo } from 'shared/src/assets/rodeo-logo.svg'

import { BackButton } from 'components/BackButton'
import { Button } from 'components/Button'
import { ShareButton } from 'components/feed/ShareButton'
import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import hamburgerMenuIcon from '../../assets/hamburger-menu-icon.svg'
import homeIcon from '../../assets/home-icon.svg'
import { addShowMenuContext } from '../../utils/menu'
import { ForegroundStyle, serifFontFamily } from '../../utils/styles'
import { GRADIENT_Z_INDEX, NAVBAR_Z_INDEX } from '../../utils/zIndex'
import { RoundupButton } from './RoundupButton'
import { SearchButton } from './SearchButton'

export type NavBackgroundStyle = 'light' | 'dark' | 'black' | 'transparent'

const useStyles = createUseStyles({
  gradient: {
    background: `linear-gradient(180deg, #000000 0.01%, rgba(0, 0, 0, 0.51) 68.98%, rgba(0, 0, 0, 0) 100%)`,
    height: 150,
    maxWidth: 428,
    width: '100vw',
    position: 'fixed',
    zIndex: GRADIENT_Z_INDEX,
  },
  nav: {
    display: 'flex',
    zIndex: NAVBAR_Z_INDEX,
    gap: 10,
    height: 51,
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'fixed',
    top: 0,
    maxWidth: 428,
    width: '100vw',
    backgroundColor: (backgroundStyle: NavBackgroundStyle) =>
      backgroundStyle === 'black'
        ? 'black'
        : backgroundStyle === 'dark'
        ? '#101010'
        : backgroundStyle === 'light'
        ? '#FDF8F2'
        : 'transparent',
  },
  homeIcon: {
    textDecoration: 'none',
    paddingTop: 18,
  },
  title: {
    fontFamily: serifFontFamily,
    color: '#ffffff',
    fontSize: 24,
    fontWeight: 600,
    letterSpacing: '0.01em',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  titleItalic: {
    fontStyle: 'italic',
  },
  rightButtons: {
    display: 'flex',
    justifyContent: 'center',
    paddingRight: 8,
  },
  leftButtons: {
    display: 'flex',
    justifyContent: 'center',
    paddingLeft: 8,
  },
  placeholder: {
    width: 44,
  },
  roundupIcon: {
    cursor: 'pointer',
  },
  searchIcon: {
    cursor: 'pointer',
  },
})

export interface NavProps {
  foregroundStyle?: ForegroundStyle
  backgroundStyle?: NavBackgroundStyle
  titleTranslationKey?: string
  title?: string
  CenterComponent?: FC
  shareCreatorId?: number
  showProfileButton?: boolean
  showHomeButton?: boolean
  showLogo?: boolean
  showGradient?: boolean
  showRoundupButton?: boolean
  className?: string
}

export const NavBar: FC<NavProps> = flowMax(
  addDisplayName('NavBar'),
  addDefaultProps({
    foregroundStyle: 'light',
    showGradient: false,
    showHomeButton: true,
    showLogo: false,
    showRoundupButton: false,
  }),
  addShowMenuContext,
  addNavigationState('showBackButton', 'setShowBackButton', false),
  addProps(({ backgroundStyle }) => ({
    classes: useStyles(backgroundStyle),
  })),
  addTranslationHelpers,
  ({
    foregroundStyle,
    titleTranslationKey: titleTranlationKey,
    title,
    CenterComponent,
    showBackButton,
    showProfileButton,
    showHomeButton,
    showLogo,
    showGradient,
    showRoundupButton,
    shareCreatorId,
    toggleMenu,
    classes,
    className,
    t,
  }) => (
    <>
      {showGradient && <div className={classes.gradient} />}
      <div className={cx(classes.nav, className)}>
        {showBackButton ? (
          <BackButton foregroundStyle={foregroundStyle} />
        ) : showProfileButton ? (
          <div className={classes.leftButtons}>
            <Button onClick={toggleMenu}>
              <img src={hamburgerMenuIcon} alt={t('menuAlt')} />
            </Button>
            <div className={classes.placeholder} />
          </div>
        ) : showHomeButton ? (
          <Link to="/" className={classes.homeIcon}>
            <img src={homeIcon} alt={t('menuAlt')} />
          </Link>
        ) : (
          <div className={classes.placeholder} />
        )}
        {titleTranlationKey ? (
          <span className={classes.title}>
            <Trans i18nKey={titleTranlationKey as any} t={t}>
              <span className={classes.titleItalic} />
            </Trans>
          </span>
        ) : title ? (
          <span className={classes.title}>{title}</span>
        ) : CenterComponent ? (
          <CenterComponent />
        ) : null}
        {showLogo ? (
          <RodeoLogo
            style={{
              fill: 'white',
              width: 72,
              height: 72,
            }}
          />
        ) : (
          <></>
        )}
        <div className={classes.rightButtons}>
          {showRoundupButton && (
            <RoundupButton className={classes.roundupIcon} />
          )}
          <SearchButton className={classes.searchIcon} />
          {shareCreatorId && (
            <ShareButton
              context={{
                creatorId: shareCreatorId,
              }}
              size={40}
            />
          )}
        </div>
      </div>
    </>
  )
)
